import api from '@/api'

let updateChecking = false
let updateChecked = false
let downloadTask = null
let updateOverlay = null
let updateOverlayStatus = null
let updateOverlayProgress = null
let lastDownloadPercent = -1
let shownDownloadPercent = 0
let targetDownloadPercent = 0
let progressTimer = null
let forcedInstallFile = ''
let forcedInstallConfig = null
let installOpening = false
let resumeListenerBound = false
let backButtonLocked = false

/**
 * 功能：更新遮罩显示期间拦截 Android 返回键，避免用户退出更新流程。
 * 参数：无。
 */
function lockAndroidBackButton() {
  if (backButtonLocked || !window.plus || !window.plus.key) return
  window.plus.key.addEventListener('backbutton', preventAndroidBack)
  backButtonLocked = true
}

/**
 * 功能：更新流程允许退出时恢复 Android 返回键默认行为。
 * 参数：无。
 */
function unlockAndroidBackButton() {
  if (!backButtonLocked || !window.plus || !window.plus.key) return
  window.plus.key.removeEventListener('backbutton', preventAndroidBack)
  backButtonLocked = false
}

/**
 * 功能：拦截更新流程中的 Android 返回键。
 * 参数：无。
 */
function preventAndroidBack() {}

/**
 * 功能：判断当前页面是否运行在 HBuilder 打包的 Android App 中。
 * 参数：无。
 */
function isAndroidApp() {
  const plus = window.plus
  return Boolean(
    plus &&
    plus.os &&
    String(plus.os.name || '').toLowerCase() === 'android'
  )
}

/**
 * 功能：等待 H5+ 环境准备完成，仅在 Android App 中执行回调。
 * 参数：callback H5+ Android 环境准备完成后的回调方法。
 */
function onAndroidPlusReady(callback) {
  if (isAndroidApp()) {
    callback()
    return
  }

  const handlePlusReady = () => {
    document.removeEventListener('plusready', handlePlusReady, false)
    if (isAndroidApp()) callback()
  }
  document.addEventListener('plusready', handlePlusReady, false)
}

/**
 * 功能：读取当前安装包的 Android versionCode。
 * 参数：plus H5+ 全局对象。
 */
function getCurrentVersionCode(plus) {
  return new Promise(resolve => {
    try {
      plus.runtime.getProperty(plus.runtime.appid, property => {
        resolve(Number.parseInt(property.versionCode, 10) || 0)
      })
    } catch (error) {
      resolve(0)
    }
  })
}

/**
 * 功能：创建不可关闭的更新遮罩，用于下载和强制安装期间锁定 App。
 * 参数：无。
 */
function ensureUpdateOverlay() {
  if (updateOverlay) return

  updateOverlay = document.createElement('div')
  Object.assign(updateOverlay.style, {
    position: 'fixed',
    zIndex: '2147483647',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    boxSizing: 'border-box',
    background: 'rgba(0, 0, 0, 0.62)'
  })

  const dialog = document.createElement('div')
  Object.assign(dialog.style, {
    width: '100%',
    maxWidth: '320px',
    padding: '24px 20px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    background: '#ffffff',
    textAlign: 'center',
    boxShadow: '0 12px 36px rgba(0, 0, 0, 0.28)'
  })

  const title = document.createElement('div')
  title.textContent = '正在更新'
  Object.assign(title.style, {
    color: '#222222',
    fontSize: '18px',
    fontWeight: '700'
  })

  updateOverlayStatus = document.createElement('div')
  Object.assign(updateOverlayStatus.style, {
    marginTop: '12px',
    color: '#555555',
    fontSize: '14px',
    lineHeight: '1.6'
  })

  const progressTrack = document.createElement('div')
  Object.assign(progressTrack.style, {
    height: '8px',
    marginTop: '18px',
    overflow: 'hidden',
    borderRadius: '4px',
    background: '#eeeeee'
  })

  updateOverlayProgress = document.createElement('div')
  Object.assign(updateOverlayProgress.style, {
    width: '0%',
    height: '100%',
    background: '#b3262d',
    transition: 'width 0.2s ease'
  })

  progressTrack.appendChild(updateOverlayProgress)
  dialog.appendChild(title)
  dialog.appendChild(updateOverlayStatus)
  dialog.appendChild(progressTrack)
  updateOverlay.appendChild(dialog)
  document.body.appendChild(updateOverlay)
}

/**
 * 功能：显示不可关闭的更新遮罩并更新提示文字和进度。
 * 参数：message 遮罩提示文字；percent 下载进度，范围为 0 到 100。
 */
function showUpdateOverlay(message, percent = 0) {
  ensureUpdateOverlay()
  updateOverlayStatus.textContent = message
  updateOverlayProgress.style.width = `${Math.max(0, Math.min(100, percent))}%`
  updateOverlay.style.display = 'flex'
  lockAndroidBackButton()
}

/**
 * 功能：关闭更新遮罩并恢复 App 操作。
 * 参数：无。
 */
function closeUpdateOverlay() {
  if (updateOverlay) updateOverlay.style.display = 'none'
  unlockAndroidBackButton()
  stopSmoothProgress()
  lastDownloadPercent = -1
}

/**
 * 功能：显示下载进度，并在下载期间阻止操作 App 页面。
 * 参数：task 当前下载任务。
 */
function renderDownloadProgress(percent) {
  showUpdateOverlay(`正在下载安装包 ${percent}%`, percent)
}

/**
 * 功能：停止平滑进度定时器。
 * 参数：无。
 */
function stopSmoothProgress() {
  if (!progressTimer) return
  window.clearInterval(progressTimer)
  progressTimer = null
}

/**
 * 功能：重置并启动平滑下载进度，未完成前最多展示到 99%。
 * 参数：无。
 */
function startSmoothProgress() {
  stopSmoothProgress()
  shownDownloadPercent = 0
  targetDownloadPercent = 0
  renderDownloadProgress(0)

  progressTimer = window.setInterval(() => {
    targetDownloadPercent = Math.min(99, targetDownloadPercent + 1)
    if (shownDownloadPercent >= targetDownloadPercent) return
    shownDownloadPercent += 1
    renderDownloadProgress(shownDownloadPercent)
  }, 150)
}

/**
 * 功能：根据真实下载进度抬高展示进度目标，展示层仍逐格上涨。
 * 参数：percent 真实下载进度百分比。
 */
function syncRealDownloadProgress(percent) {
  const nextTarget = Math.max(0, Math.min(99, percent))
  targetDownloadPercent = Math.max(targetDownloadPercent, nextTarget)
}

/**
 * 功能：真实下载完成时立刻展示 100%。
 * 参数：无。
 */
function finishDownloadProgress() {
  stopSmoothProgress()
  shownDownloadPercent = 100
  targetDownloadPercent = 100
  renderDownloadProgress(100)
}

/**
 * 功能：绑定下载任务进度，使用平滑展示进度并在真实完成前停在 99%。
 * 参数：task 当前下载任务。
 */
function bindDownloadProgress(task) {
  startSmoothProgress()

  task.addEventListener('statechanged', currentTask => {
    if (!currentTask.totalSize) return
    const percent = Math.min(
      99,
      Math.floor((currentTask.downloadedSize / currentTask.totalSize) * 100)
    )
    if (percent === lastDownloadPercent) return
    lastDownloadPercent = percent
    syncRealDownloadProgress(percent)
  })
}

/**
 * 功能：监听 App 从系统安装器返回事件，强制更新未完成时再次打开安装器。
 * 参数：plus H5+ 全局对象。
 */
function bindForcedInstallResume(plus) {
  if (resumeListenerBound) return
  resumeListenerBound = true
  document.addEventListener('resume', () => {
    if (!forcedInstallFile || !forcedInstallConfig || installOpening) return
    showUpdateOverlay('必须完成安装后才能继续使用', 100)
    window.setTimeout(() => installApk(plus, forcedInstallFile, forcedInstallConfig), 500)
  })
}

/**
 * 功能：下载失败后提示用户重新下载。
 * 参数：plus H5+ 全局对象；config 服务端更新配置。
 */
function showDownloadRetry(plus, config) {
  const buttons = config.androidForceUpdate
    ? ['重新下载']
    : ['重新下载', '稍后更新']

  plus.nativeUI.confirm(
    '安装包下载失败，请检查网络后重试。',
    event => {
      if (event.index === 0) {
        downloadAndInstall(plus, config)
      } else if (config.androidForceUpdate) {
        window.setTimeout(() => showDownloadRetry(plus, config), 300)
      }
    },
    '更新失败',
    buttons
  )
}

/**
 * 功能：调用 Android 系统安装界面安装已经下载完成的 APK。
 * 参数：plus H5+ 全局对象；fileName 已下载 APK 文件路径；config 服务端更新配置。
 */
function installApk(plus, fileName, config) {
  if (config.androidForceUpdate) {
    forcedInstallFile = fileName
    forcedInstallConfig = config
    showUpdateOverlay('下载完成，请在系统安装页面完成更新', 100)
    bindForcedInstallResume(plus)
  } else {
    closeUpdateOverlay()
  }

  installOpening = true
  plus.runtime.install(
    fileName,
    {},
    () => {
      installOpening = false
    },
    error => {
      installOpening = false
      plus.nativeUI.alert(
        `无法打开安装程序：${error.message || '请检查安装未知应用权限'}`,
        () => showDownloadRetry(plus, config),
        '安装失败',
        '重新下载'
      )
    }
  )
  window.setTimeout(() => {
    installOpening = false
  }, 1500)
}

/**
 * 功能：下载新版 APK，下载完成后自动打开 Android 系统安装界面。
 * 参数：plus H5+ 全局对象；config 服务端更新配置。
 */
function downloadAndInstall(plus, config) {
  if (downloadTask) return

  const downloadUrl = String(config.androidDownloadUrl || '').trim()
  if (!downloadUrl) {
    plus.nativeUI.alert('新版安装包暂未配置，请稍后再试。', null, '更新失败', '确定')
    return
  }

  const installConfig = {
    ...config,
    androidForceUpdate: true
  }

  downloadTask = plus.downloader.createDownload(
    downloadUrl,
    {
      filename: `_downloads/lottery-update-${installConfig.androidVersionCode}.apk`,
      retry: 3
    },
    (task, status) => {
      downloadTask = null
      if (status === 200) {
        finishDownloadProgress()
        installApk(plus, task.filename, installConfig)
        return
      }
      closeUpdateOverlay()
      showDownloadRetry(plus, installConfig)
    }
  )

  bindDownloadProgress(downloadTask)
  downloadTask.start()
}

/**
 * 功能：显示 Android 新版本提示，并根据是否强制更新提供操作按钮。
 * 参数：plus H5+ 全局对象；config 服务端更新配置。
 */
function showUpdateConfirm(plus, config) {
  const versionName = String(config.androidVersionName || '').trim()
  const updateContent = String(config.androidUpdateContent || '').trim()
  const message = [
    versionName ? `发现新版本 ${versionName}` : '发现新版本',
    updateContent || '建议立即更新以获得更好的使用体验。'
  ].join('\n\n')
  const buttons = config.androidForceUpdate
    ? ['立即更新']
    : ['立即更新', '稍后更新']

  plus.nativeUI.confirm(
    message,
    event => {
      if (event.index === 0) {
        downloadAndInstall(plus, config)
      } else if (config.androidForceUpdate) {
        window.setTimeout(() => showUpdateConfirm(plus, config), 300)
      }
    },
    config.androidForceUpdate ? '需要更新' : '版本更新',
    buttons
  )
}

/**
 * 功能：在 HBuilder Android App 启动时检查服务器是否存在更高版本。
 * 参数：无。
 */
export function checkAndroidAppUpdate() {
  if (updateChecking || updateChecked) return

  onAndroidPlusReady(async () => {
    if (updateChecking || updateChecked) return
    updateChecking = true

    try {
      const plus = window.plus
      const currentVersionCode = await getCurrentVersionCode(plus)
      const response = await api.getAppInstallConfig()
      if (!response || !response.__success || !response.data) return

      updateChecked = true
      const serverVersionCode = Number.parseInt(response.data.androidVersionCode, 10) || 0
      if (!currentVersionCode || serverVersionCode <= currentVersionCode) return
      showUpdateConfirm(plus, response.data)
    } catch (error) {
      // 请求工具已负责展示网络错误，此处仅避免更新检查影响 App 启动。
    } finally {
      updateChecking = false
    }
  })
}
